import type {
  GetAssetPageMarkersProps,
  GetCurrentMarkerProps,
} from "./asset-page-map.type";
import type { AssetMarker } from "@/entities/asset";
import type { ContextSlug } from "@/entities/context";
import type { LocationDetail } from "@/entities/location";
import type { MapMarker } from "@/modules/map";

import { isSector } from "@/entities/location";
import { parseLatLngString } from "@/modules/map";
import {
  getAssetMarker,
  getLocationMarker,
  getSectorMarker,
  MARKER_ASSET_SCALE,
} from "@/modules/marker";

export const getAssetPageMarkers = ({
  context,
  location,
  asset,
  assetColor,
  components,
}: GetAssetPageMarkersProps): MapMarker[] => {
  const locationMarker = getParentMarker(context, location);
  const assetMarker = getCurrentMarker({
    context,
    asset,
    color: assetColor,
    components,
  });

  if (!assetMarker && !locationMarker) return [];

  const assetMarkers = assetMarker ? [assetMarker] : [];

  if (!locationMarker) return assetMarkers;

  return [{ ...locationMarker, children: assetMarkers }];
};

export const getCurrentMarker = ({
  context,
  asset,
  color,
  components,
}: GetCurrentMarkerProps) => {
  const position = parseLatLngString({
    coordinate: asset?.coordinate ?? "",
  });

  if (!asset || !position) return null;

  const children = getChildMarkers(context, components);

  return getAssetMarker({
    context,
    model: {
      id: asset.id,
      name: asset.name,
    },
    position,
    color,
    scale: MARKER_ASSET_SCALE.LG,
    children,
  });
};

export const getParentMarker = (
  context: ContextSlug | null,
  location?: LocationDetail
) => {
  const position = parseLatLngString({
    coordinate: location?.coordinate ?? "",
  });

  if (!location || !position) return null;

  const { id, name } = location;
  const model = { id, name };

  return isSector(location)
    ? getSectorMarker({ context, model, position })
    : getLocationMarker({ context, model, position });
};

export const getChildMarkers = (
  context: ContextSlug | null,
  components: AssetMarker[]
) => {
  const markers = components.map((component) =>
    getAssetMarker({
      context,
      model: {
        id: component.id,
        name: component.name,
      },
      position: {
        lat: parseFloat(component.lat),
        lng: parseFloat(component.lng),
      },
    })
  );

  return markers.filter((marker) => marker !== null);
};
